import React, { useEffect, useState } from "react";
import DeckGL from "@deck.gl/react";
import { PointCloudLayer } from "@deck.gl/layers";
import { OrbitView } from "@deck.gl/core";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import PositionIcon from "../assets/icons/Vector.svg";
import { fetchPointCloud3D } from '../APIs/apiService';

// Fix per le icone di Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Zoom controls component for OrbitView
function OrbitViewZoomControls({ viewState, setViewState }) {
  const handleZoomIn = () => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom + 1, 20),
    }));
  };

  const handleZoomOut = () => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom - 1, -5),
    }));
  };

  const baseStyle = {
    backgroundColor: "white",
    color: "#333",
    border: "1px solid rgba(0,0,0,0.2)",
    borderRadius: "6px",
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <button onClick={handleZoomIn} title="Zoom in" style={baseStyle}>
        +
      </button>
      <button onClick={handleZoomOut} title="Zoom out" style={baseStyle}>
        -
      </button>
    </div>
  );
}

// Current location button for OrbitView
function OrbitViewCurrentLocationButton({ viewState, setViewState }) {
  const handleClick = () => {
    if (!navigator.geolocation) {
      alert("Geolocalizzazione non supportata");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        // Convert lat/lng to the coordinate system used by the point cloud
        // This is a simplified conversion - you might need to adjust based on your data
        const x = longitude * 1000; // Scale factor
        const y = latitude * 1000; // Scale factor
        const z = 0; // Ground level

        setViewState((prev) => ({
          ...prev,
          target: [x, y, z],
          zoom: 2,
        }));
      },
      (error) => {
        console.error("Error getting location:", error);
        alert("Impossibile ottenere la tua posizione.");
      }
    );
  };

  const buttonStyle = {
    backgroundColor: "white",
    color: "#2d3b2d",
    border: "1px solid rgba(0,0,0,0.2)",
    borderRadius: "10px",
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
  };

  return (
    <button
      onClick={handleClick}
      title="Vai alla mia posizione"
      style={buttonStyle}
    >
      <img
        src={PositionIcon}
        alt="Current location"
        style={{ width: 20, height: 20 }}
      />
    </button>
  );
}

// Map controls wrapper for OrbitView
function OrbitViewMapControls({ viewState, setViewState }) {
  return (
    <div
      style={{
        position: "absolute",
        right: 16,
        bottom: 170,
        zIndex: 1200,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        alignItems: "flex-end",
      }}
    >
      <OrbitViewCurrentLocationButton
        viewState={viewState}
        setViewState={setViewState}
      />
      <OrbitViewZoomControls
        viewState={viewState}
        setViewState={setViewState}
      />
    </div>
  );
}

export default function PointCloudDeckGL() {
  const [data, setData] = useState(null);
  const [viewState, setViewState] = useState(null);

  useEffect(() => {
    fetchPointCloud3D()
      .then((json) => {
        const points = json.Location.map((loc, i) => ({
          position: loc,
          color: json.Color[i],
        }));
        setData(points);

        let minX = Infinity,
          maxX = -Infinity;
        let minY = Infinity,
          maxY = -Infinity;
        let minZ = Infinity,
          maxZ = -Infinity;

        for (let loc of json.Location) {
          const [x, y, z] = loc;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          if (z < minZ) minZ = z;
          if (z > maxZ) maxZ = z;
        }

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        const centerZ = (minZ + maxZ) / 2;
        setViewState({
          target: [centerX, centerY, centerZ],
          zoom: 2,
          rotationX: 60,
          rotationOrbit: 30,
          minZoom: -5,
          maxZoom: 20,
        });
      });
  }, []);

  const layer =
    data &&
    new PointCloudLayer({
      id: "point-cloud-layer",
      data,
      getPosition: (d) => d.position,
      getColor: (d) => d.color,
      sizeUnits: "meters",
      pointSize: 0.1,
      pickable: true,
      onClick: (info) => {
        if (info.object) {
          // console.log("Hai cliccato il punto:", info.object);
        }
      },
    });

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        backgroundColor: "#111",
      }}
    >
      {viewState && (
        <DeckGL
          views={new OrbitView()}
          viewState={viewState}
          onViewStateChange={({ viewState }) => setViewState(viewState)}
          controller={true}
          layers={[layer]}
          style={{ width: "100%", height: "100%" }}
        />
      )}

      {viewState && (
        <OrbitViewMapControls
          viewState={viewState}
          setViewState={setViewState}
        />
      )}

      <div
        style={{
          position: "absolute",
          bottom: 170,
          right: 70,
          zIndex: 1000,
          background: "white",
          padding: 8,
          borderRadius: 4,
          border: "1px solid #ccc",
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          width: 200,
          height: 150,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            marginBottom: 4,
            color: "#333",
            fontSize: 12,
          }}
        >
          Via della Chiappella
        </div>
        <div
          style={{
            width: "100%",
            height: 120,
            border: "1px solid #ddd",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <MapContainer
            center={[44.40859260427418, 8.907047510147097]}
            zoom={17}
            style={{ height: "100%", width: "100%" }}
            zoomControl={false}
            dragging={false}
            scrollWheelZoom={false}
            doubleClickZoom={false}
            touchZoom={false}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
              opacity={1}
            />
            <Marker position={[44.40859260427418, 8.907047510147097]} />
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
